import { gql, useMutation } from '@apollo/client';
import { ActionContext } from '@ovComponents/5-page/viewPage/actionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { usePermissions } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { withRetry } from '@utils/retry';
import Queue from 'promise-queue';
import { useContext, useEffect, useState } from 'react';
import {
  NEXT_STEP,
  PREV_STEP,
  UPDATE_WORKFLOW_COMPLETION,
} from './subSteps/queries';
import { WorkflowCompletionVisual } from './workflowCompletion.visual';
import { WorkflowContext } from './workflowCompletionContext';
import type { WorkflowDataType } from './workflowTypes';
import { reconcileWorkflowAccountContext } from './workflowTypes';

export type { WorkflowContextType } from './workflowCompletionContext';
export type {
  CreatedAccountsType,
  CustodianDepositTransferContext,
  CustodianDepositTransferContextInput,
  CustodianDepositTransferContextResponse,
  TransferContextData,
  WorkflowCustodianDepositTransfer,
  WorkflowDataType,
} from './workflowTypes';

export {
  buildClearedCreatedFields,
  CREATED_OBJECT_FIELDS,
  reconcileWorkflowAccountContext,
  toTransferContextData,
} from './workflowTypes';

export { WorkflowContext };

export const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
        incompleteFields
        iDCheckCompleted
        iDVerified
        inProvinceSince
        hasCheckedPolicyAndCondition
        affiliateOnly
      }
    }
  }
`;

const workflowCompletionUpdateQueue = new Queue(1);

export const WorkflowCompletion = ({
  workflowCompletion,
  open,
  onClose,
  isInnerWorkflow,
  updateParent,
  onCloseIconClick = () => onClose(),
}: {
  workflowCompletion: any;
  open: boolean;
  onClose: () => void;
  isInnerWorkflow?: boolean;
  updateParent?: (activeCompletion?: any) => void;
  onCloseIconClick?: () => any;
}) => {
  const [workflowData, setWorkflowDataState] = useState<WorkflowDataType>(
    workflowCompletion?.context ?? {},
  );
  const { refreshWidgets } = useContext(ActionContext);
  const setWorkflowData = async (
    action:
      | WorkflowDataType
      | ((prevState: WorkflowDataType) => WorkflowDataType),
  ) => {
    await new Promise<void>((resolve) => {
      setWorkflowDataState((previousData) => {
        const newData =
          typeof action === 'function' ? action(previousData) : action;
        if (
          newData &&
          Object.keys(newData).length > 0 &&
          workflowCompletion?.id
        ) {
          withRetry(
            () =>
              new Promise<void>((innerResolve, innerReject) => {
                updateWorkflowCompletion({
                  variables: {
                    input: {
                      workflowCompletionId: workflowCompletion.id,
                      context: newData,
                    },
                  },
                  onCompleted: () => innerResolve(),
                  onError: (error: Error) => innerReject(error),
                });
              }),
            3, // maxRetries
            500, // delayMs
          ).then(resolve);
        }
        return newData;
      });
    });
  };
  const [activeWorkflowCompletion, setActiveWorkflowCompletion] =
    useState<any>(workflowCompletion);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { permissions } = usePermissions();
  const [stepEnteredAt, setStepEnteredAt] = useState<number>(Date.now());
  const [hasCompletedWorkflow, setHasCompletedWorkflow] =
    useState<boolean>(false);

  const getWorkflowType = (workflowCompletionItem: any): string => {
    const rawWorkflowType =
      workflowCompletionItem?.workflow?.type ??
      workflowCompletionItem?.workflow?.workflowType ??
      workflowCompletionItem?.workflow?.key ??
      workflowCompletionItem?.workflow?.name;
    return typeof rawWorkflowType === 'string'
      ? rawWorkflowType.toLowerCase().replaceAll(' ', '_')
      : 'unknown';
  };

  const getStepName = (workflowCompletionItem: any): string =>
    workflowCompletionItem?.currentSubStep?.type ??
    workflowCompletionItem?.currentSubStep?.options?.title?.en ??
    'unknown';

  const checkIfItIsFirstWorkflow = (activeWorkflowCompletionItem: any) => {
    if (Object.keys(activeWorkflowCompletionItem).length === 0) return;
    if (
      activeWorkflowCompletionItem.currentStep.id ===
        activeWorkflowCompletionItem.workflow.steps[0].id &&
      activeWorkflowCompletionItem.currentSubStep.id ===
        activeWorkflowCompletionItem.workflow.steps[0].subSteps[0].id
    ) {
      sendAnalytic(ovAnalyticsEvents.workflowsCompletionStart, {
        workflowStepTitle:
          activeWorkflowCompletionItem.currentSubStep.options?.title,
        stepName: getStepName(activeWorkflowCompletionItem),
        workflowType: getWorkflowType(activeWorkflowCompletionItem),
        workflowId: activeWorkflowCompletionItem?.workflow?.id,
        workflowName: activeWorkflowCompletionItem?.workflow?.name,
        activeWorkflowCompletionId: activeWorkflowCompletionItem?.id,
        objectId: activeWorkflowCompletionItem?.objectId,
        objectType: activeWorkflowCompletionItem?.objectType,
      });
    }
  };
  const trackWorkflowStepAbandonment = (exitMethod: 'close' | 'back') => {
    if (!hasCompletedWorkflow && activeWorkflowCompletion?.id) {
      sendAnalytic('workflow_step_abandoned', {
        activeWorkflowCompletionId: activeWorkflowCompletion.id,
        workflowId: activeWorkflowCompletion?.workflow?.id,
        workflowName: activeWorkflowCompletion?.workflow?.name,
        workflowType: getWorkflowType(activeWorkflowCompletion),
        stepName: getStepName(activeWorkflowCompletion),
        timeOnStep: Date.now() - stepEnteredAt,
        exitMethod,
      });
    }
  };
  const handleClose = () => {
    trackWorkflowStepAbandonment('close');
    onClose?.();
  };

  const [updateIdCheckWithComplianceReview] = useMutation(UPDATE_USER, {
    variables: {
      input: {
        userId: workflowCompletion.objectId,
        iDCheckCompleted: true,
        complianceState: 'NEEDS_REVIEW',
        complianceIssueSource: 'User opted out of ID verification',
      },
    },
    onCompleted: (): void => {},
  });

  const [nextStepMutation, { loading }] = useMutation(NEXT_STEP(permissions));
  const nextStep: typeof nextStepMutation = async (
    ...args: Parameters<typeof nextStepMutation>
  ) => {
    const previousStepName = getStepName(activeWorkflowCompletion);
    try {
      const result = await nextStepMutation(...args);
      const { data } = result;
      // Action steps are always skipped (we don't return them). If we do, it means that it is the last substep.
      const isActionStep =
        data.nextSubStep.workflowCompletion.currentSubStep.type.includes(
          'ACTION',
        );
      const isLastSubStep =
        data.nextSubStep.workflowCompletion.currentSubStep.id ===
        activeWorkflowCompletion.currentSubStep.id;

      const reconciledContext = reconcileWorkflowAccountContext(
        data.nextSubStep.workflowCompletion.context,
      );
      const reconciledWorkflowCompletion = {
        ...data.nextSubStep.workflowCompletion,
        context: reconciledContext,
      };

      setWorkflowDataState(reconciledContext);
      if (updateParent) {
        await updateParent(reconciledWorkflowCompletion);
      }

      if (isLastSubStep || isActionStep) {
        setHasCompletedWorkflow(true);
        sendAnalytic(ovAnalyticsEvents.workflowsCompletionEnd, {
          workflowStepTitle:
            data.nextSubStep.workflowCompletion.currentSubStep.options?.title,
          stepName: getStepName(data.nextSubStep.workflowCompletion),
          workflowType: getWorkflowType(data.nextSubStep.workflowCompletion),
          workflowId: data.nextSubStep.workflowCompletion?.workflow?.id,
          workflowName: data.nextSubStep.workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: data.nextSubStep.workflowCompletion?.id,
          objectId: data.nextSubStep.workflowCompletion?.objectId,
          objectType: data.nextSubStep.workflowCompletion?.objectType,
        });
        handleClose();
        !isInnerWorkflow && refreshWidgets?.();
        if (updateParent) {
          await updateParent();
        }
      }

      setActiveWorkflowCompletion({
        ...reconciledWorkflowCompletion,
        direction: 'FORWARD',
      });
      setStepEnteredAt(Date.now());

      return result;
    } catch (error: any) {
      sendAnalytic('workflow_error_displayed', {
        errorType: error?.message ?? 'unknown',
        stepName: previousStepName,
        workflowType: getWorkflowType(activeWorkflowCompletion),
        activeWorkflowCompletionId: activeWorkflowCompletion?.id,
      });
      throw error;
    }
  };

  const [previousStep, { loading: prevLoading }] = useMutation(
    PREV_STEP(permissions),
    {
      variables: {
        input: { workflowCompletionId: workflowCompletion.id },
      },
      onCompleted: (data) => {
        setActiveWorkflowCompletion({
          ...data.previousSubStep.workflowCompletion,
          direction: 'BACKWARD',
        });
      },
    },
  );

  const [updateWorkflowCompletionMutation] = useMutation(
    UPDATE_WORKFLOW_COMPLETION(permissions),
    {
      variables: {
        input: {
          workflowCompletionId: workflowCompletion.id,
          context: workflowData,
        },
      },
    },
  );
  const updateWorkflowCompletion = (
    ...args: Parameters<typeof updateWorkflowCompletionMutation>
  ) =>
    workflowCompletionUpdateQueue.add(() =>
      updateWorkflowCompletionMutation(...args),
    );

  useEffect(() => {
    setActiveWorkflowCompletion(workflowCompletion);
    setHasCompletedWorkflow(false);
    setStepEnteredAt(Date.now());
    checkIfItIsFirstWorkflow(workflowCompletion);
    if (workflowCompletion.context) {
      setWorkflowDataState({
        ...workflowCompletion.context,
        currentUserId: workflowCompletion.objectId,
      });
    }
  }, [workflowCompletion]);

  const goNext = async (fromKey?: string) => {
    if (fromKey && typeof fromKey === 'string') {
      await nextStep({
        variables: {
          input: { workflowCompletionId: workflowCompletion.id, fromKey },
        },
      });
    } else {
      await nextStep({
        variables: { input: { workflowCompletionId: workflowCompletion.id } },
      });
    }
  };

  return (
    <WorkflowContext.Provider
      value={{
        workflowData,
        setWorkflowData,
        updateWorkflowContext: updateWorkflowCompletion,
      }}
    >
      <WorkflowCompletionVisual
        onCloseIconClick={() => {
          trackWorkflowStepAbandonment('close');
          onCloseIconClick();
        }}
        workflowCompletion={activeWorkflowCompletion}
        open={open}
        onClose={handleClose}
        onNext={goNext}
        previousStep={async () => {
          trackWorkflowStepAbandonment('back');
          await previousStep();
          setStepEnteredAt(Date.now());
        }}
        loading={loading || prevLoading}
        setActiveWorkflowCompletion={setActiveWorkflowCompletion}
        isInnerWorkflow={isInnerWorkflow}
        handleIdSkip={() => updateIdCheckWithComplianceReview()}
      />
    </WorkflowContext.Provider>
  );
};
